import type React from "react";

import { Briefcase, MapPin, Users, Building2 } from "lucide-react";
import {
  MessageSquare,
  Share2,
  ThumbsUp,
  TrendingUp,
  // PieChart,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shadcnComponents/ui/card";
import { Badge } from "@/shadcnComponents/ui/badge";
import {
  IDistributionItem,
  IMetric,
  IReachAudienceAnalysisProps,
} from "../CampaignInterface/LiveCampaignInterface";

// Reusable Components
const MetricCard = ({ value, label, icon, color }: IMetric) => (
  <Card
    className={`bg-gradient-to-br from-${color}-50 to-${color}-50 border-${color}-100 flex-1`}
  >
    <CardHeader className="pb-1 pt-2 px-3">
      <CardTitle className={`text-xs font-medium text-${color}-700`}>
        {label}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0 px-3 pb-2">
      <div className="flex items-center justify-between">
        <div className={`text-lg font-bold text-${color}-900`}>
          {typeof value === "number" && !isNaN(value)
            ? label === "Likes:Comments Ratio" || label === "Engagement Rate"
              ? value.toFixed(2)
              : value
            : value}
          {label === "Engagement Rate" && "%"}
        </div>
        {icon}
      </div>
    </CardContent>
  </Card>
);

const DistributionItem = ({ item }: { item: IDistributionItem }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <div
          className="h-3 w-3 rounded-full"
          style={{ backgroundColor: item.color }}
        ></div>
        <span className="text-slate-700">{item.name}</span>
      </div>
      <span className="font-medium text-slate-900">{item.numericalValues}</span>
    </div>
    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{
          width: `${item.value}%`,
          backgroundColor: item?.color,
        }}
      ></div>
    </div>
  </div>
);

const DistributionCard = ({
  title,
  description,
  icon,
  items,
  color,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  items: IDistributionItem[];
  color: string;
}) => (
  <Card className={`border-${color}-100 overflow-hidden`}>
    <CardHeader
      className={`bg-gradient-to-r from-${color}-50 to-${
        color === "blue"
          ? "cyan"
          : color === "purple"
          ? "indigo"
          : color === "green"
          ? "emerald"
          : "orange"
      }-50`}
    >
      <CardTitle
        className={`text-xs font-medium text-${color}-800 flex items-center gap-2`}
      >
        {icon}
        {title}
      </CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent className="pt-6 space-y-4">
      {items.map((item, index) => (
        <DistributionItem key={index} item={item} />
      ))}
    </CardContent>
  </Card>
);

export const ReachAudienceAnalysis = ({
  metrics,
  summary,
  distribution,
}: IReachAudienceAnalysisProps) => {
  // Create metrics objects array for easy mapping
  const metricCards: IMetric[] = [
    {
      value: metrics?.er,
      label: "Engagement Rate",
      icon: <TrendingUp className="h-4 w-4 text-blue-600" />,
      color: "blue",
    },
    {
      value: metrics?.likes,
      label: "Likes",
      icon: <ThumbsUp className="h-4 w-4 text-green-600" />,
      color: "green",
    },
    {
      value: metrics?.comments,
      label: "Comments",
      icon: <MessageSquare className="h-4 w-4 text-purple-600" />,
      color: "purple",
    },
    {
      value: metrics?.reposts,
      label: "Reposts",
      icon: <Share2 className="h-4 w-4 text-amber-600" />,
      color: "amber",
    },
    {
      value: metrics?.lcr,
      label: "Likes:Comments Ratio",
      icon: (
        <div className="flex items-center gap-1">
          <ThumbsUp className="h-3 w-3 text-cyan-600" />
          <span className="text-xs text-cyan-600">:</span>
          <MessageSquare className="h-3 w-3 text-cyan-600" />
        </div>
      ),
      color: "cyan",
    },
    {
      value: metrics?.commentLikes
        ? Math.round(metrics?.commentLikes * 0.15)
        : 0,
      label: "Comment Likes",
      icon: (
        <div className="flex items-center">
          <ThumbsUp className="h-3 w-3 text-rose-600" />
          <MessageSquare className="h-3 w-3 text-rose-600 ml-0.5" />
        </div>
      ),
      color: "rose",
    },
  ];

  // Define summary badges
  const summaryBadges = [
    {
      value: summary?.location,
      label: "Location",
      icon: <MapPin className="h-3.5 w-3.5" />,
      color: "blue",
    },
    {
      value: summary?.role,
      label: "Role",
      icon: <Briefcase className="h-3.5 w-3.5" />,
      color: "purple",
    },
    {
      value: summary?.seniority,
      label: "Seniority",
      icon: <Users className="h-3.5 w-3.5" />,
      color: "green",
    },
  ];
  // Define distribution cards
  const distributionCards = [
    {
      title: "Job Roles Distribution",
      description: "Breakdown by professional roles",
      icon: <Briefcase className="h-4 w-4" />,
      items: distribution?.role,
      color: "purple",
    },
    {
      title: "Location Distribution",
      description: "Breakdown by geographic regions",
      icon: <MapPin className="h-4 w-4" />,
      items: distribution?.location,
      color: "green",
    },
    {
      title: "Industry Distribution",
      description: "Breakdown by business sectors",
      icon: <Building2 className="h-4 w-4" />,
      items: distribution?.industries,
      color: "amber",
    },
    {
      title: "Seniority Distribution",
      description: "Breakdown by career levels",
      icon: <Users className="h-4 w-4" />,
      items: distribution?.seniority,
      color: "blue",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
          Reach Audience Analysis
        </h2>
        <div className="flex items-center gap-2">
          {metrics?.impressions > 0 && (
            <Badge variant="outline" className="border-cyan-500 text-cyan-500">
              Total Reach: {metrics.impressions}
            </Badge>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="w-full overflow-x-auto">
        <div className="flex gap-3 mb-6 min-w-[900px]">
          {metricCards.map(
            (metric, index) =>
              metric?.value > 0 && <MetricCard key={index} {...metric} />
          )}
        </div>
      </div>

      {/* Reach Summary */}
      <Card className="border-blue-100 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
          <CardTitle className="text-blue-800">Reach Summary</CardTitle>
          <CardDescription>
            Key insights of the audience who saw the campaign
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3 mb-4">
            {summaryBadges?.map(
              (badge, index) =>
                badge.value && (
                  <Badge
                    key={index}
                    variant="outline"
                    className={`flex items-center gap-1.5 border-${badge.color}-300 bg-${badge.color}-50 text-${badge.color}-700`}
                  >
                    {badge.icon}
                    {badge.label}: {badge.value}
                  </Badge>
                )
            )}
          </div>

          {summary?.insights && (
            <div className="rounded-lg border p-4 bg-blue-50">
              <h4 className="font-medium text-blue-800 mb-2">
                Audience Insights
              </h4>
              <p className="text-sm text-slate-700">{summary.insights}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Distribution Grids */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {distributionCards?.map(
          (card, index) =>
            card?.items?.length > 0 && (
              <DistributionCard key={index} {...card} />
            )
        )}
      </div>

      {/* Audience Overview */}
      {/* <Card className="border-purple-100">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardTitle className="text-purple-800">Audience Overview</CardTitle>
          <CardDescription>
            Summary of key audience characteristics
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {metrics?.impressions > 0 && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="relative h-60 w-60">
                    <PieChart className="h-full w-full text-slate-200" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-xl font-bold text-purple-800">
                          {metrics.impressions}
                        </div>
                        <div className="text-xs text-slate-500">
                          Total Reach
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg border p-4 bg-purple-50">
              <h4 className="font-medium text-purple-800 mb-2">
                Key Takeaways
              </h4>
              <ul className="space-y-2 text-sm text-slate-700">
                {[
                  summary?.role &&
                    `Highest engagement from ${summary.role}s in the ${summary.location} region.`,
                  summary?.seniority &&
                    `${summary.seniority} professionals show the strongest interest in the content.`,
                  metrics?.er &&
                    `Content resonates well with the target demographic, as shown by the ${metrics?.er?.toFixed(
                      2
                    )}% engagement rate.`,
                  // summary?.role &&
                  //   `Consider creating more content tailored to ${summary.role}s to maximize engagement.`,
                ]
                  .filter(Boolean)
                  .map((text, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="mt-0.5 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs">
                        {index + 1}
                      </div>
                      <p>{text}</p>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
};
